import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowLeft, Ban, Loader2 } from 'lucide-react'
import { useContact } from '@/hooks/useApi'
import { useDoNotContact } from '@/hooks/useDoNotContact'
import { formatDate } from '@/lib/format'
import { ContactEmailList } from '@/components/ContactEmailList'
import { ContactEnrollmentList } from '@/components/ContactEnrollmentList'
import { ContactCompany } from '@/components/CompanyLogo'
import { contactCompanyName } from '@/lib/company'

export function ContactDetail() {
  const { t } = useTranslation(['contacts', 'common'])
  const { id } = useParams<{ id: string }>()
  const { data: contact, isLoading, error } = useContact(id)
  const doNotContact = useDoNotContact()
  const [confirmOpen, setConfirmOpen] = useState(false)

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-6 w-24 mb-6" />
        <Skeleton className="h-10 w-64 mb-2" />
        <Skeleton className="h-5 w-48 mb-6" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  if (error || !contact) {
    return (
      <div>
        <Link
          to="/contacts"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('detail.back')}
        </Link>
        <p className="text-muted-foreground">{t('detail.notFound')}</p>
      </div>
    )
  }

  // Truthy, not `!== null`: an API that does not know the mark yet (the
  // moment of a deploy) sends no field, and that is not a marked contact
  const marked = !!contact.do_not_contact_at
  const contactName = contact.full_name || contact.email
  const handleConfirm = () => {
    const done = () => setConfirmOpen(false)
    if (marked) {
      doNotContact.unmark(contact.id, done)
    } else {
      doNotContact.mark(contact.id, done)
    }
  }

  return (
    <div>
      <Link
        to="/contacts"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('detail.back')}
      </Link>

      <div className="flex justify-between items-start mb-6 pb-4 border-b">
        <div>
          <h1 className="text-2xl font-bold">{contact.full_name || contact.email}</h1>
          <p className="flex items-center gap-1.5 text-muted-foreground">
            {contact.email}
            {contactCompanyName(contact) && (
              <>
                <span aria-hidden>·</span>
                <ContactCompany contact={contact} />
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {marked && (
            <Badge variant="destructive">{t('detail.doNotContact.badge')}</Badge>
          )}
          {contact.active_enrollment_count > 0 && (
            <Badge variant="info">
              {t('detail.activeEnrollments', { count: contact.active_enrollment_count })}
            </Badge>
          )}
          {contact.hubspot_id && (
            <Badge variant="success">HubSpot</Badge>
          )}
          <Button variant="outline" size="sm" onClick={() => setConfirmOpen(true)}>
            <Ban className="h-4 w-4 mr-2" />
            {marked ? t('detail.doNotContact.remove') : t('detail.doNotContact.mark')}
          </Button>
        </div>
      </div>

      {contact.do_not_contact_at && (
        <p
          role="status"
          className="mb-6 rounded-md border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950 p-3 text-sm text-red-800 dark:text-red-200"
        >
          {t('detail.doNotContact.since', { date: formatDate(contact.do_not_contact_at) })}
        </p>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {marked ? t('detail.doNotContact.removeTitle') : t('detail.doNotContact.markTitle')}
            </DialogTitle>
            <DialogDescription>
              {marked
                ? t('detail.doNotContact.removeDescription', { name: contactName })
                : t('detail.doNotContact.markDescription', { name: contactName })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              {t('common:cancel')}
            </Button>
            <Button
              variant={marked ? 'default' : 'destructive'}
              onClick={handleConfirm}
              disabled={doNotContact.isPending}
            >
              {doNotContact.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {marked
                ? t('detail.doNotContact.removeConfirm')
                : t('detail.doNotContact.markConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="emails">
        <TabsList>
          <TabsTrigger value="emails">{t('detail.tabEmails')}</TabsTrigger>
          <TabsTrigger value="enrollments">{t('detail.tabEnrollments')}</TabsTrigger>
        </TabsList>
        <TabsContent value="emails" className="mt-4">
          <ContactEmailList contactId={contact.id} />
        </TabsContent>
        <TabsContent value="enrollments" className="mt-4">
          <ContactEnrollmentList contactId={contact.id} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
